import styled, { keyframes } from "styled-components";

export const StyledRadioWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledAutoConfirmButton = styled.button`
  color: ${(props) => (props.enabled ? "white" : "#ff6d1d")};
  background-color: ${(props) => (props.enabled ? "#ff6d1d" : "white")};
  border-radius: 6px;
  border: 1px solid #ff6d1d;
  font-weight: 600;
  padding: 4px 12px;
  cursor: pointer;
  margin-left: 20px;
  &:hover {
    opacity: 0.8;
  }
`;

export const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const fadeIn = keyframes`
  0% { 
    opacity: 0; 
    transform: translateY(10px); 
  }
  100% { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

// Global shimmer animation for "AI is thinking" button
export const globalShimmer = keyframes`
  0% { 
    transform: translateX(-100%); 
  }
  100% { 
    transform: translateX(100%); 
  }
`;

export const StyledClassificationSkeleton = styled.div`
  width: 710px;
  height: 79px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%);
  padding: 16px;
  background-repeat: no-repeat;
  background-size: 300px 100%;
  animation: ${shimmer} 1.8s ease-in-out infinite;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.8),
      transparent
    );
    animation: ${shimmer} 2s ease-in-out infinite;
  }
`;

export const StyledClassifiedNameSelect = styled.select`
  padding: 12px 35px 12px 19px;
  border-radius: 6px;
  font-family: "SF Pro Text";
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid #ebe9f1;
  outline: none;
  position: relative;
  display: inline-block;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg width='7' height='5' viewBox='0 0 7 5' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.60293 0.5C0.748577 0.5 0.287669 1.50212 0.843675 2.15079L2.95517 4.6142C3.35426 5.07981 4.07459 5.07981 4.47368 4.6142L6.58518 2.15079C7.14118 1.50212 6.68027 0.5 5.82592 0.5H1.60293Z' fill='%23B9B9C3'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 7px 5px;

  &:focus {
    border-color: #ff6d1d;
  }
`;

export const StyledCustomRadio = styled.input.attrs({ type: "radio" })`
  appearance: none;
  border: 1px solid #ff6d1d;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 109, 29, 0.1);
  }

  ${({ confirmed }) =>
    confirmed &&
    `
    &::before {
      content: "";
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      width: 12px;
      height: 12px;
      background-color: #FF6D1D;
      border-radius: 50%;
      transform: translate(-50%, -50%);
    }
  `}

  ${({ autoConfirmHovered, wouldBeConfirmed, confirmed }) =>
    autoConfirmHovered && wouldBeConfirmed && !confirmed &&
    `
    background-color: rgba(255, 109, 29, 0.1);
  `}
`;

export const StyledIframeSkeleton = styled.div`
  margin: 0 72px;
  width: 90%;
  min-height: 700px;
  background: linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%);
  background-repeat: no-repeat;
  background-size: 400px 100%;
  animation: ${shimmer} 2.2s ease-in-out infinite;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.7),
      transparent
    );
    animation: ${shimmer} 2.5s ease-in-out infinite;
  }
`;

export const StyledPdfThumbnailSkeleton = styled.div`
  width: 38px;
  height: 48px;
  background-color: #f0f0f0;
  background-image: linear-gradient(
    to right,
    #f0f0f0 0%,
    #ffffff 20%,
    #f0f0f0 40%,
    #f0f0f0 100%
  );
  background-repeat: no-repeat;
  background-size: 200px 100%;
  animation: ${shimmer} 1.2s infinite linear;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledThresholdInput = styled.input`
  width: 60px;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  font-family: "SF Pro Text";
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  outline: none;
  transition: all 0.2s ease;
  background-color: white;
  color: #333;

  &:focus {
    border-color: #ff6d1d;
    box-shadow: 0 0 0 2px rgba(255, 109, 29, 0.1);
  }

  &:hover {
    border-color: #d0d0d0;
  }

  /* Remove spinner arrows for webkit browsers */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Remove spinner arrows for Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

export const ClassifiedSplitFadeIn = styled.div`
  animation: ${fadeIn} 0.5s ease-out forwards;
  margin-bottom: 8px;
`;

export const AIThinkingButton = styled.div`
  width: 100%;
  height: 48px;
  background-color: #fff3ec;
  color: #FF6D1D;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  margin: 48px;
  border: 1.5px solid #fff3ec;
  cursor: not-allowed;
  opacity: 0.5;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 109, 29, 0.3) 50%, transparent 100%);
    animation: ${globalShimmer} 1.5s infinite;
    z-index: 1;
  }

  span {
    position: relative;
    z-index: 2;
  }
`;
